import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom'; // ✅ Import ajouté pour récupérer le contexte
import TaskColumn from './TaskColumn';
import { DragDropContext } from '@hello-pangea/dnd';
import axios from 'axios';
import { motion } from "framer-motion";
import logo from "../../image/progress1.gif";
import api from '../../api';

// Composant Modal de Confirmation
const ConfirmationModal = ({ isOpen, onConfirm, onCancel, title, message, confirmText, cancelText, type = 'warning' }) => {
  if (!isOpen) return null;

  const getIconAndColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: '✓',
          bgColor: 'bg-green-50',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          confirmBg: 'bg-green-600 hover:bg-green-700'
        };
      case 'danger':
        return {
          icon: '⚠',
          bgColor: 'bg-red-50',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          confirmBg: 'bg-red-600 hover:bg-red-700'
        };
      case 'info':
        return {
          icon: 'ℹ',
          bgColor: 'bg-blue-50',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          confirmBg: 'bg-blue-600 hover:bg-blue-700'
        };
      default: // warning
        return {
          icon: '⚠',
          bgColor: 'bg-amber-50',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          confirmBg: 'bg-amber-600 hover:bg-amber-700'
        };
    }
  };

  const { icon, bgColor, iconBg, iconColor, confirmBg } = getIconAndColors();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className={`mx-auto flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:h-12 sm:w-12`}>
            <span className={`text-2xl font-bold ${iconColor} sm:text-xl`}>
              {icon}
            </span>
          </div>

          <div className="mt-4 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 className="text-xl font-semibold leading-6 text-gray-900 sm:text-lg">
              {title}
            </h3>
            <div className="mt-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                {message}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors sm:mt-0 sm:w-auto"
              onClick={onCancel}
            >
              {cancelText || 'Annuler'}
            </button>
            <button
              type="button"
              className={`inline-flex w-full justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors sm:w-auto ${confirmBg}`}
              onClick={onConfirm}
            >
              {confirmText || 'Confirmer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TaskBoard = () => {
  // ✅ Récupération du contexte depuis le Layout
  const context = useOutletContext();

  // ✅ Destructuration du contexte avec valeurs par défaut
  const {
    searchTerm = '',
    dateStart = null,
    dateEnd = null,
    dateStartTech = '',
    dateEndTech = '',
    dateDeadlineClientStart = '',
    dateDeadlineClientEnd = '',
    priorityFilter = null,
    typeFilter = null,
    moduleFilter = null,
    organisationFilter = null,
    deadlineTechFilter = null,
    deadlineClientFilter = null,
    selectedOrg = '',
    organisations = []
  } = context || {};

  const [allStages, setAllStages] = useState([]);
  const [permittedStages, setPermStages] = useState([]);
  const [tasks, setTasks] = useState({});

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [causeText, setCauseText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // État pour la modal de confirmation
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const [roleId, setRoleId] = useState(null);
  const [fonctionnelId, setFonctionnelId] = useState(null);
  const [testeurId, setTesteurId] = useState(null);



  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setFonctionnelId(parseInt(user.id, 10));
        setTesteurId(parseInt(user.id, 10));
        setRoleId(parseInt(user.role_id, 10));

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);



  // Fonction pour vérifier les conditions des tickets
  const isTicketValid = (ticket) => {
    const validationAdminCl = String(ticket.validation_adminCl);

    // Si c'est une demande : les 3 conditions doivent être vraies
    // Si c'est un bug : seulement validation_adminCl doit être "4"
    let isValidDemande = false;
    if (ticket.type === 'demande' && ticket.demande) {
      const confirmationAdmin = String(ticket.demande.confirmation_admin);
      const accepter = String(ticket.demande.accepter);
      isValidDemande = (confirmationAdmin === '1' && accepter === '1' && validationAdminCl === '4');
    }
    const isValidBug = (ticket.type === 'bug' && validationAdminCl === '4');

    return isValidDemande || isValidBug;
  };

  // Charger les étapes
  useEffect(() => {
    api.get('/parametrages/etapes')
      .then(res => {
        const etapes = res.data.etapes || [];
        etapes.sort((a, b) => a.order - b.order);
        setAllStages(etapes);
      })
      .catch(err => console.error('GET /etapes error:', err));
  }, []);

  // Charger les étapes autorisées
  useEffect(() => {
    if (!roleId || allStages.length === 0) return;

    api.get(`/parametrages/roles/${roleId}`)
      .then(res => {
        const role = res.data.role || res.data;
        const allowedIds = Array.isArray(role.etapes) ? role.etapes.map(e => e.id) : [];
        const filtered = allStages
          .filter(stage => allowedIds.includes(stage.id))
          .sort((a, b) => a.order - b.order);
        setPermStages(filtered);
      })
      .catch(err => console.error('Erreur GET /roles/{id} :', err));
  }, [roleId, allStages]);

  // Charger les tickets - utilise selectedOrg du contexte
  useEffect(() => {
    if (permittedStages.length === 0 || !fonctionnelId) return;

    const fetchTickets = async () => {
      const url =
        selectedOrg !== ""
          ? `/tickets/client/${selectedOrg}`
          : `/tickets/fonctionel/${fonctionnelId}`;

      try {
        const { data } = await api.get(url);
        const grouped = {};
        const tickets = data.tickets || data || [];

        tickets.forEach(ticket => {
          if (!isTicketValid(ticket)) {
            console.log("Ticket filtré:", ticket.id);
            return;
          }

          let sid = ticket.etape_id != null ? Number(ticket.etape_id) : permittedStages[0].id;

          if (!permittedStages.some(s => s.id === sid)) {
            sid = permittedStages[0].id;
          }

          grouped[sid] = grouped[sid] || [];
          grouped[sid].push({
            id: ticket.id,
            nom: ticket.nom,
            content: ticket.content,
            type: ticket.type,
            deadline_client: ticket.deadline_client,
            deadline_tech: ticket.deadline_tech,
            date_debut_tech: ticket.date_debut_tech,
            priorite: ticket.priorite,
            status: ticket.status,
            createur: ticket.createur,
            organisation: ticket.organisation,
            developpeurs: ticket.developpeurs || [],
            module: ticket.module,
            etape_id: sid,
            created_at: ticket.created_at,
            updated_at: ticket.updated_at,
            module_id: ticket.module_id,
          });
        });

        setTasks(grouped);
      } catch (err) {
        console.error("GET /tickets error:", err);
      }
    };

    fetchTickets();
    const interval = setInterval(fetchTickets, 3000);
    return () => clearInterval(interval);
  }, [selectedOrg, permittedStages, fonctionnelId]);

  // Fonction pour fermer la modal de confirmation
  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      type: 'warning'
    });
  };

  const handleConfirmReject = async () => {
    if (!pendingMove) return;

    const { ticketId, to } = pendingMove;

    setIsProcessing(true);
    try {
      await api.post(`/tickets/${ticketId}`, {
        etape_id: to,
        raison_retour: causeText,
        modifier: testeurId
      });

      setTasks(prev => {
        const updated = { ...prev };
        const srcTasks = [...(updated[pendingMove.from] || [])];
        const index = srcTasks.findIndex(t => t.id === ticketId);
        if (index === -1) return prev;

        const [movedTicket] = srcTasks.splice(index, 1);
        movedTicket.etape_id = to;
        movedTicket.raison_retour = causeText;

        const dstTasks = [...(updated[to] || [])];
        dstTasks.push(movedTicket);

        updated[pendingMove.from] = srcTasks;
        updated[to] = dstTasks;

        return updated;
      });

      setShowReturnModal(false);
      setCauseText('');
      setPendingMove(null);
    } catch (error) {
      console.error("Erreur lors du retour fonctionnel :", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Gestion du drag & drop avec modal de confirmation stylée
  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const src = Number(source.droppableId);
    const dst = Number(destination.droppableId);
    const ticketId = Number(result.draggableId);

    const movedTask = tasks[src]?.find(task => task.id === ticketId);
    const taskName = movedTask?.nom || `Ticket #${ticketId}`;

    const destinationStage = permittedStages.find(stage => stage.id === dst);
    const isLivree = destinationStage?.id === 7;
    const isRetourFonctionnel = destinationStage?.id === 8;


    const performMove = async () => {
      // Mise à jour optimiste de l'UI avant l'appel API
      setTasks(prev => {
        const newTasks = { ...prev };
        const srcTasks = [...(newTasks[src] || [])];
        const ticketIndex = srcTasks.findIndex(t => t.id === ticketId);

        if (ticketIndex === -1) return prev;

        const [movedTicket] = srcTasks.splice(ticketIndex, 1);
        movedTicket.etape_id = dst;

        if (src === dst) {
          srcTasks.splice(destination.index, 0, movedTicket);
          newTasks[src] = srcTasks;
        } else {
          const dstTasks = [...(newTasks[dst] || [])];
          dstTasks.splice(destination.index, 0, movedTicket);
          newTasks[src] = srcTasks;
          newTasks[dst] = dstTasks;
        }

        return newTasks;
      });

      try {
        // Mise à jour côté serveur
        await api.post(`/tickets/${ticketId}`, {
          status: 1,
          etape_id: dst,
          testeur_id: testeurId,
          modifier: testeurId,
        });
      } catch (err) {
        console.error("Erreur lors de la mise à jour :", err);

        // En cas d'erreur → recharger la liste avec API
        const url = selectedOrg
          ? `/tickets/client/${selectedOrg}`
          : `/tickets/fonctionnel/${fonctionnelId}`;

        try {
          const { data } = await api.get(url);
          const grouped = {};
          (data.tickets || []).forEach(ticket => {
            if (!isTicketValid(ticket)) return;
            let sid = ticket.etape_id != null ? Number(ticket.etape_id) : permittedStages[0].id;
            if (!permittedStages.some(s => s.id === sid)) {
              sid = permittedStages[0].id;
            }
            grouped[sid] = grouped[sid] || [];
            grouped[sid].push({ ...ticket, etape_id: sid });
          });
          setTasks(grouped);
        } catch (err2) {
          console.error("Erreur lors du rechargement des tickets:", err2);
        }
      }

      closeConfirmationModal();
    };

    if (isLivree) {
      setConfirmationModal({
        isOpen: true,
        title: 'Confirmer la livraison',
        message: `Êtes-vous sûr de vouloir marquer le ticket "${taskName}" comme livré ? Cette action changera définitivement le statut du ticket.`,
        onConfirm: performMove,
        type: 'success'
      });
    }
    else if (isRetourFonctionnel) {
      setPendingMove({
        ticket: movedTask,
        ticketId,
        from: src,
        to: dst
      });
      setShowReturnModal(true);
    } else {
      performMove();
    }
  };

  // ✅ Fonction de vérification des dates - améliorée pour gérer null
  const isDateInRange = (dateToCheck, startDate, endDate) => {
    if (!dateToCheck) return !startDate && !endDate;

    const checkDate = new Date(dateToCheck);

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      checkDate.setHours(0, 0, 0, 0);
      if (checkDate < start) return false;
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      checkDate.setHours(0, 0, 0, 0);
      if (checkDate > end) return false;
    }

    return true;
  };

  // Fonction pour normaliser les dates
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    return dateStr.split('T')[0];
  };

  // ✅ Filtrage des tâches - utilise les filtres du contexte
  const filteredTasks = permittedStages.reduce((acc, stage) => {
    acc[stage.id] = (tasks[stage.id] || []).filter(task => {
      // Filtre par texte - recherche dans tous les champs
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const searchableFields = [
          task.nom,
          task.content,
          task.type,
          task.priorite?.toString(),
          task.status,
          task.organisation?.nomOrganisation,
          task.module?.nom,
          task.createur?.prenom,
          task.createur?.nom,
          `${task.createur?.prenom || ''} ${task.createur?.nom || ''}`.trim(),
          task.deadline_client,
          task.deadline_tech,
          task.date_debut_tech,
          task.id?.toString()
        ];

        const found = searchableFields.some(field =>
          field && field.toLowerCase().includes(searchLower)
        );

        if (!found) return false;
      }

      // ✅ Filtre par priorité - gérer null correctement
      if (priorityFilter !== null && priorityFilter !== '' && task.priorite?.toString() !== priorityFilter) {
        return false;
      }

      // ✅ Filtre par type - gérer null correctement
      if (typeFilter !== null && typeFilter !== '' && task.type !== typeFilter) {
        return false;
      }

      // ✅ Filtre par module - gérer null correctement
      if (moduleFilter !== null && moduleFilter !== '' && String(task.module_id || '') !== moduleFilter) {
        return false;
      }

      // ✅ Filtre par organisation - gérer null correctement
      if (organisationFilter !== null && organisationFilter !== '' && task.organisation?.id?.toString() !== organisationFilter) {
        return false;
      }

      // ✅ Filtre par échéance technique spécifique - gérer null correctement
      if (deadlineTechFilter !== null && deadlineTechFilter !== '') {
        const taskDeadlineTech = normalizeDate(task.deadline_tech);
        if (taskDeadlineTech !== deadlineTechFilter) {
          return false;
        }
      }

      // ✅ Filtre par échéance client spécifique - gérer null correctement
      if (deadlineClientFilter !== null && deadlineClientFilter !== '') {
        const taskDeadlineClient = normalizeDate(task.deadline_client);
        if (taskDeadlineClient !== deadlineClientFilter) {
          return false;
        }
      }

      // Filtres par plages de dates
      // Date début tech
      if (dateStartTech || dateEndTech) {
        if (!isDateInRange(task.date_debut_tech, dateStartTech, dateEndTech)) {
          return false;
        }
      }

      // Deadline client (plage)
      if (dateDeadlineClientStart || dateDeadlineClientEnd) {
        if (!isDateInRange(task.deadline_client, dateDeadlineClientStart, dateDeadlineClientEnd)) {
          return false;
        }
      }

      // ✅ Filtre général (pour rétrocompatibilité) - gérer null correctement
      if (dateStart !== null || dateEnd !== null) {
        const generalDateMatch = [
          task.deadline_client,
          task.deadline_tech,
          task.date_debut_tech,
          normalizeDate(task.created_at),
          normalizeDate(task.updated_at)
        ].some(date => isDateInRange(date, dateStart, dateEnd));

        if (!generalDateMatch) return false;
      }

      return true;
    });

    return acc;
  }, {});

  return (
    <div className="flex min-h-[calc(100vh-64px)] flex-col">
      {isProcessing && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[60]">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="bg-white/90 rounded-xl p-8 shadow-lg border border-terrecuite-100 max-w-sm w-full mx-4"
          >
            <div className="flex flex-col items-center text-center space-y-5">
              {/* Logo animé avec conteneur ajusté */}
              <motion.div
                className="w-20 h-20 mb-3"
                animate={{
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  scale: {
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }
                }}
              >
                <img
                  src={logo}
                  alt="Chargement en cours"
                  className="w-full h-full object-contain"
                />
              </motion.div>

              {/* Texte avec hiérarchie visuelle améliorée */}
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-terrecuite-700">
                  Traitement en cours...
                </h3>
                <p className="text-terrecuite-500/90 text-sm leading-relaxed">
                  Nous traitons votre demande, merci de patienter.
                </p>
              </div>

              {/* Barre de progression subtile avec plus de transparence */}
              <div className="w-full bg-terrecuite-100/50 rounded-full h-1.5 mt-2">
                <motion.div
                  className="bg-terrecuite-500/80 h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: ["0%", "70%", "100%", "0%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <div className="flex items-center mb-4">
              <span className="text-red-500 text-xl mr-3">⚠</span>
              <h3 className="text-lg font-semibold text-gray-800">Retour Fonctionnel</h3>
            </div>
            <p className="text-gray-600 mb-3">
              Veuillez saisir une raison pour retourner le ticket <strong>"{pendingMove?.ticket?.nom}"</strong>.
            </p>

            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
              rows={4}
              placeholder="Raison du retour..."
              value={causeText}
              onChange={(e) => setCauseText(e.target.value)}
              disabled={isProcessing}
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowReturnModal(false); setCauseText(''); }}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                disabled={!causeText.trim() || isProcessing}
                onClick={handleConfirmReject}
                className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 ${causeText.trim() && !isProcessing
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-red-300 cursor-not-allowed"
                  }`}
              >
                {isProcessing ? (
                  <>
                    <motion.div
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Traitement...
                  </>
                ) : (
                  "Retourner"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}



      {/* Modal de Confirmation */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onCancel={closeConfirmationModal}
        type={confirmationModal.type}
        confirmText="Confirmer"
        cancelText="Annuler"
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <div className={`p-4 overflow-x-auto min-h-[calc(100vh-64px)] ${isMobile ? 'flex flex-col gap-3' : 'flex gap-4'}`}>
          {permittedStages.map(stage => (
            <TaskColumn
              key={stage.id}
              droppableId={String(stage.id)}
              title={stage.nom}
              count={filteredTasks[stage.id]?.length || 0}
              tasks={filteredTasks[stage.id] || []}
              couleur={stage.couleur}
              isMobile={isMobile}
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

export default TaskBoard;